import {
  BankAccount,
  Category,
  DashboardData,
  Newspaper,
  Notification,
  Publication,
  PublicationQuote,
  PublicationStatus,
  Transaction,
  TrendData,
  WalletData,
  WalletTransaction,
} from '../models/models';
import {
  ApiBankAccount,
  ApiCategory,
  ApiDashboard,
  ApiNewspaperPartner,
  ApiNotification,
  ApiPaymentEntry,
  ApiPublication,
  ApiPublicationChannel,
  ApiPublicationQuote,
  ApiWalletLedger,
  ApiWalletSummary,
} from './api.types';

const CATEGORY_COLORS = [
  'bg-teal-100 text-teal-600',
  'bg-blue-100 text-blue-600',
  'bg-amber-100 text-amber-600',
  'bg-purple-100 text-purple-600',
  'bg-lime-100 text-lime-600',
];

const CHART_COLORS = ['#0D4A44', '#F4A100', '#3B82F6', '#EF4444', '#A855F7', '#10B981'];

export function mapPublicationStatus(
  status: string,
  channels?: ApiPublicationChannel[] | null,
): PublicationStatus {
  // A query on any channel needs the applicant's attention now, regardless of
  // where the publication otherwise sits -- this outranks the coarser status.
  if (channels?.some((c) => c.status === 'queried')) {
    return 'Queried';
  }

  switch (status) {
    case 'draft':
      return 'Draft';
    // Not yet paid: still waiting on the applicant (payment) or on internal
    // validation before it can even reach a reviewer.
    case 'submitted':
    case 'awaiting_validation':
    case 'awaiting_payment':
      return 'Pending';
    // Paid and released to newspapers/NPR reviewers -- there is nothing left
    // for the applicant to do here, which is a meaningfully different wait
    // than "Pending" above. Grouped together because the publication-level
    // API status doesn't distinguish "paid" from "a reviewer has it" any
    // further than this -- see PublicationChannelStatus for that detail.
    case 'paid':
    case 'processing':
    case 'partially_published':
    case 'partially_completed':
      return 'Under Review';
    case 'completed':
      return 'Published';
    case 'archived':
      return 'Expired';
    default:
      return 'Pending';
  }
}

/**
 * The precise state to show as badge text -- 'Paid', 'Submitted', 'Awaiting
 * Payment', and so on -- as distinct from `mapPublicationStatus`'s coarser
 * grouping, which several of those collapse into for color/tab purposes.
 */
export function mapPublicationStatusLabel(
  status: string,
  channels?: ApiPublicationChannel[] | null,
): string {
  if (channels?.some((c) => c.status === 'queried')) {
    return 'Needs Reply';
  }

  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function newspaperInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
}

/** Slug → portal public logo path for seeded partners (and common short names). */
const NEWSPAPER_LOGO_BY_NAME: Record<string, string> = {
  'vanguard print': '/images/newspapers/vanguard-print.png',
  vanguard: '/images/newspapers/vanguard-print.png',
  'the guardian nigeria': '/images/newspapers/the-guardian-nigeria.png',
  'the guardian': '/images/newspapers/the-guardian-nigeria.png',
  guardian: '/images/newspapers/the-guardian-nigeria.png',
  'thisday print': '/images/newspapers/thisday-print.png',
  thisday: '/images/newspapers/thisday-print.png',
  'the punch print': '/images/newspapers/the-punch-print.png',
  'the punch': '/images/newspapers/the-punch-print.png',
  punch: '/images/newspapers/the-punch-print.png',
  'leadership newspaper': '/images/newspapers/leadership-newspaper.png',
  leadership: '/images/newspapers/leadership-newspaper.png',
  'businessday print': '/images/newspapers/businessday-print.png',
  businessday: '/images/newspapers/businessday-print.png',
  'daily sun print': '/images/newspapers/daily-sun-print.png',
  'daily sun': '/images/newspapers/daily-sun-print.png',
  'daily trust': '/images/newspapers/daily-trust.png',
};

export function resolveNewspaperLogoUrl(
  name: string | null | undefined,
  logoUrl?: string | null,
): string | null {
  const fromApi = logoUrl?.trim();
  if (fromApi) return fromApi;
  if (!name) return null;
  return NEWSPAPER_LOGO_BY_NAME[name.trim().toLowerCase()] ?? null;
}

export function mapPublication(api: ApiPublication): Publication {
  const content = api.current_version?.content ?? {};
  const title =
    (content['title'] as string | undefined) ??
    (content['new_name'] as string | undefined) ??
    (content['former_name'] as string | undefined) ??
    // The list endpoint doesn't load current_version, so fall back to the
    // title stashed in metadata at creation time.
    (api.metadata?.['title'] as string | undefined) ??
    'Untitled';

  const newspaper =
    (api.metadata?.['newspaper'] as string | undefined) ??
    (api.metadata?.['newspaper_name'] as string | undefined) ??
    '—';

  const channels =
    api.channels?.map((c) => c.name ?? c.channel_type ?? c.type).filter(Boolean) as string[] ??
    (Array.isArray(api.metadata?.['channels'])
      ? (api.metadata?.['channels'] as string[])
      : ['Online']);

  const bodyRaw =
    (content['body'] as string | undefined) ??
    (content['content'] as string | undefined) ??
    (content['description'] as string | undefined) ??
    (content['notice'] as string | undefined);

  const skipKeys = new Set(['title', 'body', 'content', 'description', 'notice', 'new_name', 'former_name']);
  const contentEntries = Object.entries(content)
    .filter(([key, value]) => {
      if (skipKeys.has(key)) return false;
      if (value == null) return false;
      const text = Array.isArray(value) ? value.join(', ') : String(value);
      return text.trim().length > 0;
    })
    .map(([key, value]) => ({
      key,
      label: key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      value: Array.isArray(value) ? value.join(', ') : String(value),
    }));

  return {
    id: String(api.id),
    referenceNo: api.reference ?? '',
    title,
    category: api.category?.name ?? '',
    newspaper,
    newspaperLogo: newspaperInitials(newspaper === '—' ? 'NPR' : newspaper),
    newspaperLogoUrl: resolveNewspaperLogoUrl(newspaper === '—' ? null : newspaper),
    newspaperColor: 'bg-teal-800',
    channels,
    status: mapPublicationStatus(api.status, api.channels),
    statusLabel: mapPublicationStatusLabel(api.status, api.channels),
    rawStatus: api.status,
    // The frozen quote, never metadata.amount: that is the wizard's
    // pre-pricing estimate (partner base + 2.5%, no platform base, no VAT) and
    // showed applicants less than they had actually paid.
    amount: Number(api.quote?.total ?? 0) || 0,
    date: formatDate(api.updated_at ?? api.created_at),
    createdAt: api.updated_at ?? api.created_at,
    body: bodyRaw?.trim() ? bodyRaw.trim() : undefined,
    contentEntries: contentEntries.length ? contentEntries : undefined,
  };
}

function mapFieldOptions(options: unknown): { label: string; value: string }[] {
  if (!Array.isArray(options)) return [];
  return options.map((o) => {
    if (o && typeof o === 'object') {
      const rec = o as Record<string, unknown>;
      const value = String(rec['value'] ?? rec['label'] ?? '');
      const label = String(rec['label'] ?? rec['value'] ?? '');
      return { label, value };
    }
    return { label: String(o), value: String(o) };
  });
}

export function mapCategory(api: ApiCategory, index: number): Category {
  const meta = api.metadata ?? {};
  const fields = (api.field_definitions ?? []).map((f, i) => ({
    key: f.field_key,
    label: f.label,
    type: f.type,
    required: !!f.is_required,
    validationRules: f.validation_rules ?? [],
    options: mapFieldOptions(f.options),
    order: f.display_order ?? i,
  }));
  fields.sort((a, b) => a.order - b.order);

  const documents = (api.document_requirements ?? []).map((d) => ({
    documentType: d.document_type,
    label: d.label,
    required: !!d.is_required,
    allowedFormats: d.allowed_formats ?? [],
    maxSizeKb: d.max_size_kb ?? 5120,
  }));

  return {
    id: String(api.id),
    name: api.name,
    description: api.description ?? '',
    icon: (meta['icon'] as string | undefined) ?? 'document-text',
    color: (meta['color'] as string | undefined) ?? CATEGORY_COLORS[index % CATEGORY_COLORS.length],
    fieldDefinitions: fields,
    documentRequirements: documents,
    requiresNewspaper: !!meta['requires_newspaper'],
    requiresPlatform: !!meta['requires_platform'],
    requiresEmail: !!meta['requires_email'],
    requiresDigitalGazette: !!meta['requires_digital_gazette'],
  };
}

/** API channel_type → wizard channel label keys. */
export const CHANNEL_TYPE_TO_LABEL: Record<string, string> = {
  platform: 'News and Publication Registry',
  newspaper: 'Newspaper Publication',
  email: 'Email Notification',
  digital_gazette: 'Official Gazette',
};

/**
 * Fallback channel fees, used only to hydrate a draft before the server has
 * priced it. Never authoritative: the payable amount comes from the pricing
 * engine via quote-preview / payment-summary. Kept so a restored draft can
 * render something before the first pricing call returns.
 */
export const CHANNEL_DEFAULT_FEES: Record<string, number> = {
  // Free: publishing on the portal itself carries no base charge (see
  // PricingRuleSeeder::BASE_PRICES on the API). This was previously 5000,
  // which made a restored draft's estimated total overstate itself by
  // that amount for every platform channel on the publication.
  'News and Publication Registry': 0,
  'Newspaper Publication': 10000,
  'Email Notification': 1000,
  'Official Gazette': 15000,
};

export function channelLabelFromType(type: string | undefined | null): string | null {
  if (!type) return null;
  return CHANNEL_TYPE_TO_LABEL[type] ?? null;
}

/**
 * Wizard channel label → API channel_type.
 *
 * Needed to ask the server to price a selection: the wizard tracks channels by
 * their display label, the pricing engine keys on channel_type.
 */
export const CHANNEL_LABEL_TO_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(CHANNEL_TYPE_TO_LABEL).map(([type, label]) => [label, type])
);

export function channelTypeFromLabel(label: string | undefined | null): string | null {
  if (!label) return null;
  return CHANNEL_LABEL_TO_TYPE[label] ?? null;
}

function formatReachLabel(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed && trimmed !== '[object Object]' ? trimmed : null;
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object' && 'name' in item) {
          const name = (item as { name?: unknown }).name;
          return typeof name === 'string' ? name.trim() : '';
        }
        return '';
      })
      .filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  return null;
}

export function mapNewspaper(api: ApiNewspaperPartner): Newspaper {
  // API often returns State models in `reach`; prefer names for coverage.
  const coverage =
    formatReachLabel(api.coverage) ?? formatReachLabel(api.reach) ?? 'National';

  // Est. Reach is a separate meta string when present; do not stringify State objects.
  const estReach =
    formatReachLabel(api.est_reach) ??
    (typeof api.reach === 'string' ? formatReachLabel(api.reach) : null) ??
    '—';

  return {
    id: String(api.id),
    name: api.name,
    logo: newspaperInitials(api.name),
    logoUrl: resolveNewspaperLogoUrl(api.name, api.logo_url),
    logoColor: 'bg-teal-800',
    coverage,
    category: api.category?.trim() || 'News',
    reach: estReach,
    pricePerPublication: api.price ?? 0,
    categoryRates: api.category_rates ?? {},
  };
}

const NOTIFICATION_TYPES: Notification['type'][] = ['info', 'success', 'warning', 'alert'];

export function mapNotification(api: ApiNotification): Notification {
  const type = NOTIFICATION_TYPES.includes(api.type as Notification['type'])
    ? (api.type as Notification['type'])
    : 'info';
  return {
    id: String(api.id),
    message: api.message,
    type,
    time: api.time ? formatDate(api.time) : '',
    read: !!api.read,
  };
}

function mapLedger(l: ApiWalletLedger): WalletTransaction {
  return {
    label: l.description ?? (l.type === 'credit' ? 'Wallet credit' : 'Wallet debit'),
    ref: String(l.id ?? ''),
    amount: Number(l.amount ?? 0),
    type: l.type === 'credit' ? 'credit' : 'debit',
    date: l.created_at ? formatDate(l.created_at) : '',
  };
}

export function mapBankAccount(api: ApiBankAccount): BankAccount {
  return {
    id: String(api.id),
    accountNumber: api.account_number,
    bankName: api.bank_name,
    bankCode: api.bank_code ?? null,
    accountName: api.account_name,
  };
}

export function mapWallet(
  summary: ApiWalletSummary | null,
  ledgers: ApiWalletLedger[]
): WalletData {
  return {
    balance: Number(summary?.balance ?? 0),
    monthFunded: Number(summary?.monthFunded ?? 0),
    monthSpent: Number(summary?.monthSpent ?? 0),
    transactions: ledgers.map(mapLedger),
  };
}

export function mapTransaction(api: ApiPaymentEntry): Transaction {
  const status: Transaction['status'] =
    api.status === 'paid'
      ? 'Successful'
      : api.status === 'pending'
        ? 'Pending'
        : 'Failed';
  return {
    id: String(api.id),
    reference: api.ref_id ?? '',
    publication: api.description ?? api.payment_type ?? '—',
    amount: Number(api.total ?? api.amount ?? 0),
    method: api.payment_method?.name ?? 'Paystack',
    date: formatDate(api.payment_date ?? api.created_at),
    status,
  };
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function trendWindowEnding(end: Date): Date[] {
  const endDay = startOfDay(end);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(endDay);
    d.setDate(endDay.getDate() - (6 - i));
    return d;
  });
}

function fillTrend(days: Date[], publications: Publication[]): TrendData {
  const labels = days.map((d) =>
    d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  );
  const published = days.map(() => 0);
  const pending = days.map(() => 0);
  const rejected = days.map(() => 0);
  const expired = days.map(() => 0);

  for (const pub of publications) {
    const parsed = new Date(pub.createdAt || pub.date);
    if (Number.isNaN(parsed.getTime())) continue;
    const day = startOfDay(parsed).getTime();
    const idx = days.findIndex((d) => d.getTime() === day);
    if (idx < 0) continue;

    switch (pub.status) {
      case 'Published':
      case 'Completed':
        published[idx] += 1;
        break;
      case 'Rejected':
        rejected[idx] += 1;
        break;
      case 'Expired':
        expired[idx] += 1;
        break;
      case 'Draft':
        break;
      default:
        pending[idx] += 1;
        break;
    }
  }

  return { labels, published, pending, rejected, expired };
}

/** Bucket publications into a 7-day window for the trend chart. */
function buildWeeklyTrend(publications: Publication[]): TrendData {
  const calendar = fillTrend(trendWindowEnding(new Date()), publications);
  const hasActivity = [
    ...calendar.published,
    ...calendar.pending,
    ...calendar.rejected,
    ...calendar.expired,
  ].some((n) => n > 0);
  if (hasActivity || publications.length === 0) return calendar;

  // Fallback when all pubs fall outside the current week: window on newest pub date.
  const timestamps = publications
    .map((p) => startOfDay(new Date(p.createdAt || p.date)).getTime())
    .filter((t) => !Number.isNaN(t));
  if (!timestamps.length) return calendar;
  return fillTrend(trendWindowEnding(new Date(Math.max(...timestamps))), publications);
}

export function mapDashboard(
  dashboard: ApiDashboard,
  publications: Publication[]
): DashboardData {
  const { counts } = dashboard;
  const total =
    counts.drafts +
    counts.submitted +
    counts.processing +
    counts.completed +
    counts.archived;

  const categoryMap = new Map<string, number>();
  for (const pub of publications) {
    if (!pub.category) continue;
    categoryMap.set(pub.category, (categoryMap.get(pub.category) ?? 0) + 1);
  }

  const denom = total || publications.length || 1;
  const categoryBreakdown = [...categoryMap.entries()].map(([label, value], i) => ({
    label,
    value,
    percent: Math.round((value / denom) * 1000) / 10,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const weeklyTrend = buildWeeklyTrend(publications);

  return {
    stats: {
      totalPublications: total,
      pending: counts.submitted + counts.processing,
      completed: counts.completed,
      drafts: counts.drafts,
      totalSpent: 0,
      walletBalance: 0,
    },
    monthlyTrend: weeklyTrend,
    weeklyTrend,
    categoryBreakdown,
    spendingOverview: { labels: [], data: [] },
  };
}

/**
 * Pricing-engine payload → PublicationQuote.
 *
 * Amounts arrive as strings from decimal columns, so each is coerced rather
 * than trusted to be a number.
 */
export function mapPublicationQuote(api: ApiPublicationQuote): PublicationQuote {
  const num = (v: number | string | null | undefined): number => Number(v ?? 0) || 0;

  return {
    quoteId: api.quote_id ?? null,
    subtotal: num(api.subtotal),
    serviceCharge: num(api.service_charge),
    vat: num(api.vat),
    totalAmount: num(api.total_amount),
    currency: api.currency ?? 'NGN',
    isPreview: api.is_preview ?? false,
    expiresAt: api.expires_at ?? null,
    lines: (api.lines ?? []).map((l) => ({
      channelId: l.channel_id ?? null,
      channelType: l.channel_type,
      channelName: l.channel_name,
      partnerId: l.partner_id ?? null,
      partnerName: l.partner_name ?? null,
      platformBaseAmount: num(l.platform_base_amount),
      partnerBaseAmount: num(l.partner_base_amount),
      baseAmount: num(l.base_amount),
      serviceChargeAmount: num(l.service_charge_amount),
      vatAmount: num(l.vat_amount),
      lineTotal: num(l.line_total),
    })),
  };
}
